import { useAuth } from "@wtc/ui";
import { useMutation } from "@tanstack/react-query";

/**
 * Client wiring for the "Send later" (scheduled email) flow.
 *
 * Mirrors {@link useSendMessage} but targets POST /api/emails/schedule in
 * pre-rendered mode: the composer has already resolved the template client-side,
 * so we ship the FINAL subject/body. The server re-asserts no {{variable}}
 * remains and rejects with 422 UNFILLED_TEMPLATE_VARIABLES if one does — surfaced
 * here as a {@link ScheduleEmailError} carrying `unfilledVariables`.
 */

export interface ScheduleEmailVariables {
  /** Single recipient email (server validates as one address). */
  to: string;
  cc?: string;
  bcc?: string;
  /** FINAL resolved subject — the same the immediate send would post. */
  subject: string;
  /** FINAL resolved body — the same the immediate send would post. */
  body: string;
  /** `datetime-local` wall-clock value (`YYYY-MM-DDTHH:mm`) or ISO w/ offset. */
  sendAt: string;
  /** IANA zone the offset-less `sendAt` is interpreted in (server default: Central). */
  timezone?: string;
  contactId?: string;
  connectionId?: string;
  provider?: string;
  /** Signature applied server-side at drain time; null = none. */
  signatureId?: string | null;
}

export interface ScheduleEmailResult {
  success: true;
  id: string;
  /** UTC ISO instant the email will be sent. */
  scheduledAt: string;
}

/**
 * Structured error from the schedule endpoint so callers can branch on the
 * 422 unfilled-variable case without string matching.
 */
export class ScheduleEmailError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly unfilledVariables?: string[];

  constructor(
    message: string,
    status: number,
    code?: string,
    unfilledVariables?: string[]
  ) {
    super(message);
    this.name = "ScheduleEmailError";
    this.status = status;
    this.code = code;
    this.unfilledVariables = unfilledVariables;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function readString(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

function readStringArray(value: unknown): string[] | undefined {
  return Array.isArray(value) && value.every((v) => typeof v === "string")
    ? value
    : undefined;
}

async function scheduleEmail(
  variables: ScheduleEmailVariables,
  token?: string
): Promise<ScheduleEmailResult> {
  const response = await fetch("/api/emails/schedule", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(variables),
  });

  const payload: unknown = await response.json().catch(() => null);
  const data = isRecord(payload) ? payload : {};

  if (!response.ok) {
    throw new ScheduleEmailError(
      readString(data.error) || "Failed to schedule email.",
      response.status,
      readString(data.code),
      readStringArray(data.unfilledVariables)
    );
  }

  const id = readString(data.id);
  const scheduledAt = readString(data.scheduledAt);
  if (!id || !scheduledAt) {
    throw new ScheduleEmailError(
      "The schedule service returned an unexpected response.",
      response.status
    );
  }

  return { success: true, id, scheduledAt };
}

/** Hook for scheduling an email to send at a future time. */
export function useScheduleMessage() {
  const { getAuthToken } = useAuth();
  const token = getAuthToken();

  return useMutation<ScheduleEmailResult, ScheduleEmailError, ScheduleEmailVariables>({
    mutationFn: (variables) => scheduleEmail(variables, token || undefined),
  });
}
